/**
 * Associe une adresse libre à une ville du catalogue.
 * Une région administrative (Marrakech-Safi, Casablanca-Settat, Fès-Meknès)
 * ne compte pas comme une annonce dans la ville citée seulement dans le nom de région.
 */

import { citiesSEOData } from '@/lib/data/city-seo-data';
import { cityPath } from '@/lib/seo/city-urls';

const COUNTRY = new Set(['maroc', 'morocco', 'ma']);

const ADMIN_REGIONS = new Set([
  'casablanca-settat',
  'marrakech-safi',
  'fes-meknes',
  'rabat-sale-kenitra',
  'souss-massa',
  'beni mellal-khenifra',
  'tanger-tetouan-al hoceima',
  'draa-tafilalet',
  'laayoune-sakia el hamra',
  'guelmim-oued noun',
  'dakhla-oued ed-dahab',
  'oriental',
]);

const ALIAS_TO_SLUG: Record<string, string> = {
  nouaceur: 'casablanca',
  marrakesh: 'marrakech',
  tangier: 'tanger',
  fez: 'fes',
};

/** Villes voisines raisonnables. Le lien ne redirige pas et ne promet pas de stock. */
const ALTERNATIVE_SLUGS: Record<string, readonly string[]> = {
  safi: ['marrakech'],
  settat: ['casablanca'],
  meknes: ['fes'],
  'beni-mellal': ['casablanca'],
  khemisset: ['rabat'],
  khouribga: ['casablanca'],
  ouarzazate: ['marrakech'],
};

const STREET = /\b(rue|bd|boulevard|avenue|av|route|street|chemin|impasse)\b/;

export const foldCityText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/['’]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const tokensForSlug = (slug: string): string[] => {
  const city = citiesSEOData.find((entry) => entry.slug === slug);
  if (!city) return [];
  const aliases = Object.entries(ALIAS_TO_SLUG)
    .filter(([, target]) => target === slug)
    .map(([alias]) => alias);
  const raw = [city.name, city.nameFr, city.nameEn, slug.replace(/-/g, ' '), ...aliases];
  return [...new Set(raw.map(foldCityText).filter((token) => token.length >= 3))];
};

export const slugFromCityLabel = (label: string): string | null => {
  const folded = foldCityText(label);
  if (!folded) return null;
  const city = citiesSEOData.find((entry) =>
    [entry.name, entry.nameFr, entry.nameEn, entry.slug, entry.slug.replace(/-/g, ' ')]
      .map(foldCityText)
      .includes(folded)
  );
  return city?.slug ?? ALIAS_TO_SLUG[folded] ?? null;
};

const isAdminRegion = (segment: string): boolean => {
  const folded = foldCityText(segment).replace(/\s*-\s*/g, '-');
  return ADMIN_REGIONS.has(folded);
};

const scoreToken = (segment: string, token: string): number => {
  const folded = foldCityText(segment);
  const core = folded.replace(/^\d+\s+/, '');
  if (core === token) return 3;
  if (STREET.test(folded)) return 0;
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const word = new RegExp(`(?:^|\\s)${escaped}(?:$|\\s)`);
  return word.test(core) ? 1 : 0;
};

/** Ville principale d'une adresse. Null si seule une région ou un pays est lisible. */
export const resolveListedCitySlug = (location?: string | null): string | null => {
  if (!location?.trim()) return null;
  const segments = location
    .split(',')
    .map((segment) => segment.trim())
    .filter((segment) => segment && !COUNTRY.has(foldCityText(segment)) && !isAdminRegion(segment));

  let best: { slug: string; strength: number } | null = null;
  for (const city of citiesSEOData) {
    for (const token of tokensForSlug(city.slug)) {
      for (const segment of segments) {
        const strength = scoreToken(segment, token);
        if (strength === 0) continue;
        if (!best || strength > best.strength) {
          best = { slug: city.slug, strength };
        }
      }
    }
  }
  return best?.slug ?? null;
};

export const locationMatchesListedCity = (
  location: string | null | undefined,
  cityLabel: string
): boolean => {
  const wanted = slugFromCityLabel(cityLabel);
  if (!wanted) return false;
  return resolveListedCitySlug(location) === wanted;
};

export interface CityAlternativeLink {
  name: string;
  slug: string;
  searchHref: string;
  cityHref: string;
}

export const alternativeCityLinks = (cityLabel: string): CityAlternativeLink[] => {
  const slug = slugFromCityLabel(cityLabel);
  if (!slug) return [];
  return (ALTERNATIVE_SLUGS[slug] ?? [])
    .map((alternativeSlug) => citiesSEOData.find((city) => city.slug === alternativeSlug))
    .filter((city): city is (typeof citiesSEOData)[number] => Boolean(city))
    .map((city) => ({
      name: city.nameFr,
      slug: city.slug,
      searchHref: `/search?city=${encodeURIComponent(city.name)}`,
      cityHref: cityPath(city.slug, 'fr'),
    }));
};
